import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { 
  MessageSquare, Bot, Send, HelpCircle, FileText, AlertTriangle,
  CheckCircle, Clock, ChevronDown, Search, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const faqs = [
  {
    q: "How do I connect my wallet?",
    a: "Click the 'Connect Wallet' button in the top right corner. We support Phantom, Solflare, and other major Solana wallets. Make sure you have a Solana wallet extension installed in your browser."
  },
  {
    q: "What are the trading fees?",
    a: "We charge a 1% fee on all trades. This helps maintain the platform and fund development. Priority fees go directly to validators for faster transaction processing."
  },
  {
    q: "How does the bonding curve work?",
    a: "When you buy tokens, the price increases based on a mathematical curve. When you sell, the price decreases. This ensures fair price discovery without centralized market makers."
  },
  {
    q: "What is token locking/unlocking?",
    a: "Community members can vote to lock trading on suspicious tokens or unlock previously locked tokens. This democratic system protects users from scams while being fair to legitimate projects."
  },
  {
    q: "How do I launch my own token?",
    a: "Go to Create Coin, fill in your token details (name, ticker, description, image), pay the 0.5 SOL launch fee, and your token will be live instantly with a bonding curve."
  },
  {
    q: "Why was my token locked?",
    a: "Tokens can be locked by community vote if they're suspected of being scams or violating terms. You can appeal by contacting support with evidence of legitimacy."
  },
  {
    q: "How do priority fees work?",
    a: "Priority fees allow your transactions to be processed faster during high network congestion. The fee goes to Solana validators. You can set your priority in the trading panel."
  },
  {
    q: "Can I get a refund?",
    a: "All trades are final and executed on the Solana blockchain. We cannot reverse blockchain transactions. Please trade responsibly and only with funds you can afford to lose."
  },
];

const autoResponses: Record<string, string> = {
  "wallet": "To connect your wallet, click 'Connect Wallet' in the top right. We support Phantom, Solflare, and Backpack. Need more help? Type 'human' to speak with support.",
  "fee": "Trading fees are 1% per trade. Token launch costs 0.5 SOL. Priority fees are optional and go to validators for faster processing.",
  "scam": "If you suspect a token is a scam, use the Vote panel to vote for locking. You can also report it via the flag icon on the token page.",
  "lock": "Tokens are locked by community vote to protect users. If your token was locked unfairly, contact us with evidence of legitimacy.",
  "refund": "All blockchain transactions are final and irreversible. We cannot provide refunds for trades. Please trade responsibly.",
  "launch": "To launch a token: Go to Create Coin → Fill details → Pay 0.5 SOL → Your token goes live instantly!",
  "human": "Our support team is available 9 AM - 9 PM UTC. Please describe your issue in detail and we'll respond within 24 hours.",
};

export default function Support() {
  const { toast } = useToast();
  const [messages, setMessages] = useState<Array<{ role: "user" | "bot"; text: string }>>([
    { role: "bot", text: "👋 Hi! I'm MoonBot, your automated support assistant. How can I help you today? Type your question or browse the FAQs below." }
  ]);
  const [input, setInput] = useState("");
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSend = () => {
    if (!input.trim()) return;
    
    const userMessage = input.trim().toLowerCase();
    setMessages(prev => [...prev, { role: "user", text: input }]);
    setInput("");

    // Auto-response logic
    setTimeout(() => {
      let response = "I'm not sure I understand. Try asking about: wallet connection, fees, scams, token locking, or refunds. Type 'human' for human support.";
      
      for (const [key, value] of Object.entries(autoResponses)) {
        if (userMessage.includes(key)) {
          response = value;
          break;
        }
      }

      setMessages(prev => [...prev, { role: "bot", text: response }]);
    }, 800);
  };

  const filteredFaqs = faqs.filter(faq => 
    faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmitTicket = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Ticket Submitted",
      description: "We'll respond within 24 hours. Check your email for updates.",
    });
  };

  return (
    <>
      <Helmet>
        <title>Support - TheMoonRun</title>
        <meta name="description" content="Get help with TheMoonRun platform. FAQs, automated support, and contact options." />
      </Helmet>
      
      <PageLayout>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <HelpCircle className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Help & Support</h1>
            <p className="text-muted-foreground">Get instant answers or contact our team</p>
          </div>
        </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* AI Chat */}
                <div className="rounded-xl bg-card border border-border overflow-hidden">
                  <div className="p-4 border-b border-border flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">MoonBot</h3>
                      <p className="text-xs text-muted-foreground">Automated Support • 24/7</p>
                    </div>
                    <Badge variant="secondary" className="ml-auto">Online</Badge>
                  </div>
                  
                  <div className="h-[400px] overflow-y-auto p-4 space-y-4">
                    {messages.map((msg, i) => (
                      <div key={i} className={cn(
                        "flex",
                        msg.role === "user" ? "justify-end" : "justify-start"
                      )}>
                        <div className={cn(
                          "max-w-[80%] p-3 rounded-xl text-sm",
                          msg.role === "user" 
                            ? "bg-primary text-primary-foreground" 
                            : "bg-secondary text-foreground"
                        )}>
                          {msg.text}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-4 border-t border-border">
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Type your question..." 
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                      />
                      <Button onClick={handleSend}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* FAQs */}
                <div className="space-y-6">
                  <div className="rounded-xl bg-card border border-border p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Search className="w-4 h-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search FAQs..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border-0 bg-transparent focus-visible:ring-0 p-0"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      {filteredFaqs.map((faq, i) => (
                        <div 
                          key={i} 
                          className="rounded-lg border border-border overflow-hidden"
                        >
                          <button
                            onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                            className="w-full p-3 flex items-center justify-between text-left hover:bg-secondary/50 transition-colors"
                          >
                            <span className="font-medium text-sm">{faq.q}</span>
                            <ChevronDown className={cn(
                              "w-4 h-4 text-muted-foreground transition-transform",
                              expandedFaq === i && "rotate-180"
                            )} />
                          </button>
                          {expandedFaq === i && (
                            <div className="p-3 pt-0 text-sm text-muted-foreground border-t border-border bg-secondary/30">
                              {faq.a}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Submit Ticket */}
                  <div className="rounded-xl bg-card border border-border p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      Submit a Ticket
                    </h3>
                    <form onSubmit={handleSubmitTicket} className="space-y-4">
                      <Input placeholder="Subject" required />
                      <Textarea placeholder="Describe your issue in detail..." rows={4} required />
                      <Button type="submit" className="w-full">
                        Submit Ticket
                      </Button>
                    </form>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      Response time: within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <a href="/terms" className="p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Terms of Service</p>
                    <p className="text-xs text-muted-foreground">Read our terms</p>
                  </div>
                  <ExternalLink className="w-4 h-4 ml-auto text-muted-foreground" />
                </a>
                <a href="/docs" className="p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Documentation</p>
                    <p className="text-xs text-muted-foreground">API & guides</p>
                  </div>
                  <ExternalLink className="w-4 h-4 ml-auto text-muted-foreground" />
                </a>
                <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Discord Community</p>
                    <p className="text-xs text-muted-foreground">Join our community</p>
                  </div>
                  <ExternalLink className="w-4 h-4 ml-auto text-muted-foreground" />
                </a>
              </div>
      </PageLayout>
    </>
  );
}
